import AxiosInstance from '../../helpers/axios-instance/axios-instance';
import { appConfig } from '../../helpers/app-config/app-config';
import type { Milestone, MilestonePayload } from './milestones-client.type';

const axiosInstance = AxiosInstance();
const milestonesBaseURL = appConfig.VITE_APP_API_URL + '/api/milestones/';

/**
 * Fetches milestones based on provided filters
 *
 * @param {string} organizationId - The ID of the organization (mandatory)
 * @param {string} outcomeId - The ID of the outcome (mandatory)
 * @returns {Promise<Milestone[]>} - A promise that resolves to an array of milestones
 */
const fetchMilestones = (organizationId: string, outcomeId: string): Promise<Milestone[]> => {
  const apiURL = `${milestonesBaseURL}?organization_id=${organizationId}&outcome_id=${outcomeId}`;

  return axiosInstance
    .get(apiURL)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while fetching milestones'));
};

/**
 * Creates a new milestone
 *
 * @param {Partial<MilestonePayload>} milestone - The milestone data to create
 * @returns {Promise<Milestone>} - A promise that resolves to the created milestone
 */
const createMilestone = (milestone: Partial<MilestonePayload>): Promise<Milestone> => {
  return axiosInstance
    .post(milestonesBaseURL, milestone)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while creating milestone'));
};

/**
 * Updates an existing milestone
 *
 * @param {Partial<MilestonePayload>} milestone - The milestone data to update (must include id)
 * @returns {Promise<Milestone>} - A promise that resolves to the updated milestone
 */
const updateMilestone = (milestone: Partial<MilestonePayload>): Promise<Milestone> => {
  return axiosInstance
    .put(`${milestonesBaseURL}${milestone.id}/`, milestone)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while updating milestone'));
};

/**
 * Deletes a milestone by ID
 *
 * @param {string} milestoneId - The ID of the milestone to delete
 * @returns {Promise<void>} - A promise that resolves when the milestone is deleted
 */
const deleteMilestone = (milestoneId: string): Promise<void> => {
  return axiosInstance
    .delete(`${milestonesBaseURL}${milestoneId}/`)
    .then((response) => response.data)
    .catch((error) => Promise.reject(error?.response?.data || 'Error while deleting milestone'));
};

export { createMilestone, deleteMilestone, fetchMilestones, updateMilestone };
